import { Box, Button, Flex, Portal, Text } from '@chakra-ui/react'
import { useContext, useEffect, useRef, useState } from 'react'
import Lottie from 'react-lottie-player'
import { useLocation } from 'react-router-dom'

import { isProd } from '@/utils/common'

import notRecognized from '../animations/not-recognized.json'
import { AuthContext } from '../contexts/AuthContext'
import useBrowserEnrollment from '../hooks/useBrowserEnrollment/useBrowserEnrollment'
import DOJO_MODE from '../pages/Dojo/enums/dojoMode'
import DojoContext from '../providers/DojoProvider'

const EXCLUDED_PATHS = ['/assessment', '/quiz']

const PENDING_TICKET_KEY = 'PENDING_ENROLLMENT_TICKET'

const ENROLLMENT_BLOCKER_STATES = {
    HIDDEN: 'hidden',
    REQUESTING_ENROLLMENT: 'requesting-enrollment',
    ENROLLMENT_REQUIRED: 'enrollment-required',
    PENDING_TICKET_REMINDER: 'pending-ticket-reminder',
}

const resolveEnrollmentBlockerState = ({
    isBannerEnabled,
    isExcluded,
    isPermanentEidAccepted,
    isSnoozed,
    isLoading,
    isMismatched,
    hasPendingEnrollmentTicket,
}) => {
    if (!isBannerEnabled || isExcluded || isPermanentEidAccepted || isSnoozed) {
        return ENROLLMENT_BLOCKER_STATES.HIDDEN
    }

    if (isLoading) {
        return ENROLLMENT_BLOCKER_STATES.REQUESTING_ENROLLMENT
    }

    if (isMismatched) {
        return ENROLLMENT_BLOCKER_STATES.ENROLLMENT_REQUIRED
    }

    if (hasPendingEnrollmentTicket) {
        return ENROLLMENT_BLOCKER_STATES.PENDING_TICKET_REMINDER
    }

    return ENROLLMENT_BLOCKER_STATES.HIDDEN
}

const getStudentAppUrl = () => {
    let url = import.meta.env.VITE_STUDENT_NEXT_APP_BASE_URL

    if (!url) {
        url = isProd()
            ? 'https://app.kalvium.community/'
            : 'https://dev-app.kalvium.org/'
    }

    if (!url.endsWith('/')) {
        url += '/'
    }

    return url
}

const getEnrollmentSupportUrl = () =>
    new URL('support?reason=enrollment', getStudentAppUrl()).toString()

const HibuSecurityBlocker = () => {
    const { isMismatched, isSnoozed, isPermanentEidAccepted, isBannerEnabled } =
        useBrowserEnrollment()

    const { kalviumKc } = useContext(AuthContext)
    const { store } = useContext(DojoContext) || {}
    const location = useLocation()

    const [isLoading, setIsLoading] = useState(false)
    const [hasNotSubmittedTicket, setHasNotSubmittedTicket] = useState(false)
    const hasRequestedEnrollment = useRef(false)
    const fallbackTimeoutRef = useRef(null)

    const isDojoBeltMode =
        location.pathname.startsWith('/dojo/workout') &&
        store?.mode === DOJO_MODE.BELT

    const isExcluded =
        EXCLUDED_PATHS.some(path => location.pathname.startsWith(path)) ||
        isDojoBeltMode

    useEffect(() => {
        const syncPendingTicket = () => {
            setHasNotSubmittedTicket(
                localStorage.getItem(PENDING_TICKET_KEY) === 'true'
            )
        }

        const handleMessage = event => {
            if (
                event.source === window ||
                event.origin !== new URL(getEnrollmentSupportUrl()).origin
            ) {
                return
            }

            if (event.data?.action === 'ENROLLMENT_TICKET_SUBMITTED') {
                localStorage.removeItem(PENDING_TICKET_KEY)
                setHasNotSubmittedTicket(false)
            }
        }

        const handleStorage = event => {
            if (event.key === PENDING_TICKET_KEY || event.key === null) {
                syncPendingTicket()
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncPendingTicket()
            }
        }

        syncPendingTicket()
        window.addEventListener('message', handleMessage)
        window.addEventListener('storage', handleStorage)
        window.addEventListener('focus', syncPendingTicket)
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            window.removeEventListener('message', handleMessage)
            window.removeEventListener('storage', handleStorage)
            window.removeEventListener('focus', syncPendingTicket)
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            )
            if (fallbackTimeoutRef.current) {
                clearTimeout(fallbackTimeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (
            isPermanentEidAccepted &&
            localStorage.getItem(PENDING_TICKET_KEY)
        ) {
            localStorage.removeItem(PENDING_TICKET_KEY)
            setHasNotSubmittedTicket(false)
        }
    }, [isPermanentEidAccepted])

    useEffect(() => {
        if (hasRequestedEnrollment.current && !isMismatched) {
            setIsLoading(false)
            hasRequestedEnrollment.current = false
            if (fallbackTimeoutRef.current) {
                clearTimeout(fallbackTimeoutRef.current)
                fallbackTimeoutRef.current = null
            }
        }
    }, [isMismatched])

    const blockerState = resolveEnrollmentBlockerState({
        isBannerEnabled,
        isExcluded,
        isPermanentEidAccepted,
        isSnoozed,
        isLoading,
        isMismatched,
        hasPendingEnrollmentTicket: hasNotSubmittedTicket,
    })

    if (blockerState === ENROLLMENT_BLOCKER_STATES.HIDDEN) return null

    const shouldShowPendingTicketReminder =
        blockerState === ENROLLMENT_BLOCKER_STATES.PENDING_TICKET_REMINDER

    const handleSnooze = () => {
        window.postMessage(
            {
                type: 'COMMUNICATE_TO_HIBU',
                action: 'ACTIVATE_SNOOZE',
            },
            '*'
        )
    }

    const handleRequestEnrollment = async () => {
        window.open(getEnrollmentSupportUrl(), '_blank')
        setIsLoading(true)
        hasRequestedEnrollment.current = true

        localStorage.setItem(PENDING_TICKET_KEY, 'true')
        setHasNotSubmittedTicket(true)

        try {
            if (navigator.mediaDevices?.getUserMedia) {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: true,
                })
                stream.getTracks().forEach(track => track.stop())
            }
        } catch (error) {
            console.error(
                'Camera/audio permission request failed, proceeding with enrollment.',
                { error }
            )
        }

        window.postMessage(
            {
                type: 'COMMUNICATE_TO_HIBU',
                action: 'TRIGGER_ENROLLMENT',
            },
            '*'
        )

        fallbackTimeoutRef.current = setTimeout(() => {
            setIsLoading(false)
        }, 10000)
    }

    const handleGoToSupport = () => {
        window.open(getEnrollmentSupportUrl(), '_blank')
    }

    const email = kalviumKc?.getParsedToken()?.email

    return (
        <Portal>
            <Flex
                position="fixed"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex="200000"
                bg="rgba(255, 255, 255, 0.4)"
                backdropFilter="blur(4px)"
                alignItems="center"
                justifyContent="center"
                p={4}
            >
                <Box
                    bg="white"
                    borderRadius="16px"
                    border="1px solid #E5E5E5"
                    boxShadow="0 20px 60px rgba(0, 0, 0, 0.15)"
                    maxW="440px"
                    w="full"
                    overflow="hidden"
                    position="relative"
                >
                    {/* Header with animation */}
                    <Flex
                        bg="#FEF2F2"
                        py={8}
                        justifyContent="center"
                        alignItems="center"
                        borderBottom="1px solid #CB3434"
                    >
                        <Lottie
                            loop
                            animationData={notRecognized}
                            play={true}
                            style={{ width: '140px', height: '80px' }}
                        />
                    </Flex>

                    {/* Content */}
                    <Box p={6} textAlign="center">
                        {shouldShowPendingTicketReminder ? (
                            <>
                                <Text
                                    fontSize="sm"
                                    fontWeight="normal"
                                    color="#333"
                                    lineHeight="1.6"
                                    mb={6}
                                >
                                    You&apos;ve requested browser enrollment but{' '}
                                    <Text
                                        as="span"
                                        fontWeight="bold"
                                        color="black"
                                    >
                                        haven&apos;t raised a support ticket
                                    </Text>{' '}
                                    yet. Please go to the Support page and
                                    submit your re-enrollment request to
                                    complete the process.
                                </Text>
                                <Flex
                                    direction={{ base: 'column', sm: 'row' }}
                                    gap={4}
                                    justifyContent="center"
                                    mb={3}
                                >
                                    <Button
                                        bg="black"
                                        color="white"
                                        h="40px"
                                        w="160px"
                                        borderRadius="10px"
                                        _hover={{ bg: '#222' }}
                                        onClick={handleGoToSupport}
                                        fontSize="sm"
                                        fontWeight="normal"
                                    >
                                        Go to Support
                                    </Button>
                                    <Button
                                        bg="white"
                                        color="#333"
                                        border="1px solid #ECECEC"
                                        h="40px"
                                        borderRadius="10px"
                                        _hover={{ bg: '#F7FAFC' }}
                                        onClick={handleSnooze}
                                        fontSize="sm"
                                        fontWeight="normal"
                                        boxShadow="none"
                                    >
                                        Remind me in 30 mins
                                    </Button>
                                </Flex>
                            </>
                        ) : (
                            <>
                                <Text
                                    fontSize="sm"
                                    fontWeight="normal"
                                    color="#333"
                                    lineHeight="1.6"
                                    mb={6}
                                >
                                    It looks like you are in an{' '}
                                    <Text
                                        as="span"
                                        fontWeight="bold"
                                        color="black"
                                    >
                                        unrecognized browser environment
                                    </Text>
                                    . Please use a recognised browser or enroll
                                    this browser to unleash the full experience
                                    of Kalvium.
                                </Text>
                                <Flex
                                    direction={{ base: 'column', sm: 'row' }}
                                    gap={4}
                                    justifyContent="center"
                                    mb={3}
                                >
                                    <Button
                                        bg="black"
                                        color="white"
                                        h="40px"
                                        w="160px"
                                        borderRadius="10px"
                                        _hover={{ bg: '#222' }}
                                        onClick={handleRequestEnrollment}
                                        isLoading={isLoading}
                                        isDisabled={isLoading}
                                        loadingText="Requesting..."
                                        fontSize="sm"
                                        fontWeight="normal"
                                    >
                                        Request Enrollment
                                    </Button>
                                    <Button
                                        bg="white"
                                        color="#333"
                                        border="1px solid #ECECEC"
                                        h="40px"
                                        borderRadius="10px"
                                        _hover={{ bg: '#F7FAFC' }}
                                        onClick={handleSnooze}
                                        fontSize="sm"
                                        fontWeight="normal"
                                        boxShadow="none"
                                    >
                                        Remind me in 30 mins
                                    </Button>
                                </Flex>
                            </>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box
                        bg="#FAFAFA"
                        py={3}
                        textAlign="center"
                        borderTop="1px dashed #E5E5E5"
                    >
                        <Text fontSize="xs" color="#737373">
                            You&apos;re signed in as{' '}
                            <Text as="span">{email}</Text>
                        </Text>
                    </Box>
                </Box>
            </Flex>
        </Portal>
    )
}

export default HibuSecurityBlocker
